import React from 'react';
import { PromotionSchedule } from '../../../../../lib/api/promotionSchedule';
import { STATUS_LABEL, TRACK_META, TYPE_LABEL } from '../../constants';
import {
  extractScheduleMeta,
  formatCouponBenefit,
  getDayCount,
  getMemoBody,
  getTimeRangeLabel,
  sortCouponsByBenefit,
  toDatePart,
} from '../../utils';
import {
  StyledClipNotice,
  StyledCouponRow,
  StyledDivider,
  StyledMemo,
  StyledMeta,
  StyledPeriod,
  StyledSectionLabel,
  StyledTitle,
} from './styles';

type Props = {
  schedule: PromotionSchedule;
  /**
   * 조회 구간 밖까지 이어지는 항목인지.
   *
   * 그런 막대는 드래그·리사이즈가 막혀 있는데 **화면에는 이유가 드러나지 않아**
   * 고장으로 읽힌다. 여기서 한 줄로 알린다.
   */
  isClipped?: boolean;
};

/**
 * 막대 hover 카드. 간트·캘린더가 같은 내용을 보여주도록 공용으로 둔다.
 *
 * **섹션을 나눈 이유** — 제목·기간·분류·쿠폰·메모를 같은 크기로 나열하면 7줄이 한 덩어리로 보여
 * 무엇이 무엇인지 읽히지 않는다. 특히 쿠폰이 여러 개일 때 `·` 로 이어붙이면 어디까지가 한 쿠폰인지 모른다.
 * 구분선은 두 개만 쓴다 — 더 넣으면 카드가 표처럼 무거워진다.
 */
export const ScheduleTooltip = (props: Props): React.JSX.Element => {
  const { schedule, isClipped = false } = props;

  const meta = extractScheduleMeta(schedule.memo);
  /**
   * 막대와 **같은 순서**로 보여준다.
   *
   * 서버는 couponId 오름차순으로 주는데 간트·캘린더 막대는 혜택 큰 순으로 정렬한다.
   * 카드만 서버 순서를 쓰면 등급 쿠폰(1/2/4/8%)에서 막대는 8% 부터, 카드는 1% 부터가 되어
   * 같은 항목의 같은 정보가 두 곳에서 다르게 읽힌다.
   */
  const coupons = sortCouponsByBenefit(schedule.coupons ?? []);
  // 연결이 없으면 계획 단계 표기(memo 파싱)를 대신 보여준다
  const plannedCouponLabel = coupons.length ? null : meta.couponLabel;
  const dayCount = getDayCount(schedule.startDate, schedule.endDate);
  // 서버는 ISO datetime 을 내려주므로 날짜·시각을 나눠서 보여준다 (원문을 그대로 쓰면 UTC 문자열이 노출된다)
  const timeRangeLabel = getTimeRangeLabel(schedule.startDate, schedule.endDate);
  const hasCouponSection = coupons.length > 0 || plannedCouponLabel !== null;
  // 메타 블록은 위에서 배지로 보여줬으므로 원문에서는 뺀다 (같은 문구가 두 번 나오지 않게)
  const memoBody = getMemoBody(schedule.memo);

  return (
    <>
      <StyledTitle>{schedule.title}</StyledTitle>
      <StyledPeriod>
        {toDatePart(schedule.startDate)} ~ {toDatePart(schedule.endDate)} ({dayCount}일)
        {timeRangeLabel ? ` · ${timeRangeLabel}` : ''}
      </StyledPeriod>
      <StyledMeta>
        {TRACK_META[schedule.track].label} · {STATUS_LABEL[schedule.status]}
        {schedule.type ? ` · ${TYPE_LABEL[schedule.type]}` : ''} · 담당 {schedule.adminName ?? '미지정'}
        {/* 출처(MD취합·역제안 등)는 분류의 일부다 — 쿠폰과 성격이 다르므로 여기 붙인다 */}
        {meta.sourceLabels.length > 0 ? ` · ${meta.sourceLabels.join(' + ')}` : ''}
      </StyledMeta>

      {hasCouponSection && (
        <>
          <StyledDivider />
          {coupons.length > 0 ? (
            <>
              <StyledSectionLabel>쿠폰 {coupons.length}</StyledSectionLabel>
              {coupons.map((coupon) => (
                <StyledCouponRow key={coupon.id}>
                  <span className="benefit">{formatCouponBenefit(coupon)}</span>
                  <span className="name">{coupon.name}</span>
                </StyledCouponRow>
              ))}
            </>
          ) : (
            <StyledMeta>쿠폰(계획) {plannedCouponLabel}</StyledMeta>
          )}
        </>
      )}

      {isClipped && (
        <StyledClipNotice>조회 기간 밖까지 이어집니다 — 기간을 넓히면 드래그로 조정할 수 있습니다.</StyledClipNotice>
      )}

      {memoBody && (
        <>
          <StyledDivider />
          <StyledMemo>{memoBody}</StyledMemo>
        </>
      )}
    </>
  );
};
